import { Modal, useModalContext } from '@sequencehq/core-components'
import { Footer } from '../common/Footer'
import { useAtom } from 'jotai'
import { modalScreenAtom } from '../../atoms/screens.atom'
import { useNavigate } from 'react-router-dom'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useEndOfCurrentBillingPeriod } from '../../utils/useEndOfNextBillingPeriod'
import { cancelDateSelectionAtom } from './cancel.atom'
import { ReactNode, useCallback, useMemo } from 'react'
import { match } from 'ts-pattern'
import { useMutation } from '@sequencehq/api'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import { format, parse } from '@sequencehq/utils/dates'
import { Box, Flex, Grid, GridItem, Img } from '@chakra-ui/react'
import {
  GreyGrey10,
  GreyGrey30,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyGrey90,
  Lato13Bold,
  Lato13Regular,
  Lato14Bold
} from '@sequencehq/design-tokens'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { useNotifications } from 'lib/hooks/useNotifications'
import { cubeUpdateShortcuts } from 'Cube/index'
import { getLogoSrc } from 'Customer/index'
import { parseBillingSchedulePhaseDuration } from 'lib/schedules/parseBillingSchedulePhaseDuration'
import { DATE_FORMAT_WITH_YEARS } from '../../editModalShortcuts.constants'

const useCancelConfirmationScreen = () => {
  const navigate = useNavigate()
  const modalCtx = useModalContext()
  const { displayNotification } = useNotifications()
  const [, setModalScreen] = useAtom(modalScreenAtom)
  const [cancelDateSelection] = useAtom(cancelDateSelectionAtom)
  const billingScheduleId = useBillingScheduleId()
  const { endOfBillingPeriod, isLoading: endOfBillingPeriodLoading } =
    useEndOfCurrentBillingPeriod({
      billingScheduleId
    })

  /**
   * We could do with the ability to cancel without archiving, but ho-hum
   */
  const archiveScheduleMutator = useMutation(
    dashboard20240730Client.postBillingScheduleArchive
  )

  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const phaseDurations = data.billingSchedule.phases.map(phase => {
        const startDate = parse(phase.startDate, 'yyyy-MM-dd', new Date())
        const endDate = phase.endDate
          ? parse(phase.endDate, 'yyyy-MM-dd', new Date())
          : undefined
        const duration = parseBillingSchedulePhaseDuration({
          startDate,
          endDate
        })

        return duration
      })

      return {
        startDate: parse(
          data.billingSchedule.startDate,
          'yyyy-MM-dd',
          new Date()
        ),
        endDate:
          data.billingSchedule.endDate &&
          !phaseDurations.find(d => d === 'MILESTONE')
            ? parse(data.billingSchedule.endDate, 'yyyy-MM-dd', new Date())
            : undefined,
        customerId: data.customer?.id,
        customerDomain: data.customer?.domain,
        customerName: data.customer?.legalName
      }
    }
  })

  const newEndDate = useMemo(() => {
    return match(cancelDateSelection)
      .with({ cancelDateType: 'immediate' }, () => new Date())
      .with({ cancelDateType: 'endOfBillingPeriod' }, () => endOfBillingPeriod)
      .with({ cancelDateType: 'customDate' }, ({ customDate }) => customDate)
      .exhaustive()
  }, [cancelDateSelection, endOfBillingPeriod])

  const onBack = useCallback(() => {
    setModalScreen('cancel')
  }, [setModalScreen])

  const onSubmit = useCallback(async () => {
    if (!newEndDate) {
      return
    }

    /**
     * For an immediate cancellation, we want to directly archive the schedule.
     * For the other types, we will mooch over to cube via the update shortcut path.
     */
    if (cancelDateSelection.cancelDateType === 'immediate') {
      const res = await archiveScheduleMutator.mutateAsync({
        id: billingScheduleId
      })

      if (!res) {
        displayNotification('Could not cancel schedule', { type: 'error' })
        return
      }

      displayNotification(`Schedule cancelled`, {
        type: 'success'
      })
      modalCtx.setIsOpen(false)
      return
    }

    const shortcutUuid = cubeUpdateShortcuts.set({
      type: 'cancel',
      endDate: newEndDate
    })

    navigate(
      `/billing-schedules/${billingScheduleId}/edit?${cubeUpdateShortcuts.queryParam}=${shortcutUuid}`
    )
  }, [
    navigate,
    billingScheduleId,
    newEndDate,
    modalCtx,
    archiveScheduleMutator,
    cancelDateSelection,
    displayNotification
  ])

  return {
    cancelType: cancelDateSelection.cancelDateType,
    isLoading:
      billingScheduleAggregateQuery.isLoading || endOfBillingPeriodLoading,
    customer: {
      id: billingScheduleAggregateQuery.data?.customerId,
      name: billingScheduleAggregateQuery.data?.customerName,
      logoSrc: getLogoSrc({
        domain: billingScheduleAggregateQuery.data?.customerDomain
      })
    },
    startDate: billingScheduleAggregateQuery.data?.startDate,
    originalEndDate: billingScheduleAggregateQuery.data?.endDate,
    newEndDate,
    onSubmit,
    onBack,
    submitting: archiveScheduleMutator.isPending
  }
}

const DateDisplay = (props: {
  label: string
  date: Date | undefined
  'data-testid'?: string
}) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      height="96px"
      padding="24px"
      gap="8px"
      backgroundColor={GreyGrey10}
      borderRadius="6px"
      data-testid={props['data-testid']}
    >
      <Box {...Lato13Bold} color={GreyGrey60}>
        {props.label}
      </Box>
      <Box {...Lato14Bold} color={GreyGrey80}>
        {props.date ? format(props.date, DATE_FORMAT_WITH_YEARS) : 'Ongoing'}
      </Box>
    </Flex>
  )
}

const DetailRow = (props: {
  label: ReactNode
  value: ReactNode
}) => {
  return (
    <Grid
      gridTemplateColumns="auto 1fr"
      {...Lato13Regular}
      height="32px"
      alignItems="center"
      _notLast={{
        borderBottom: `1px solid ${GreyGrey30}`
      }}
    >
      <GridItem color={GreyGrey70} height="fit-content">
        {props.label}
      </GridItem>
      <Flex height="fit-content" justifyContent="flex-end" color={GreyGrey90}>
        {props.value}
      </Flex>
    </Grid>
  )
}

export const CancelConfirmationScreen = () => {
  const {
    cancelType,
    customer,
    startDate,
    originalEndDate,
    newEndDate,
    onSubmit,
    onBack,
    submitting
  } = useCancelConfirmationScreen()

  return (
    <>
      <Modal.Content>
        <Grid
          gridTemplateAreas={`
          "originalEndDate newEndDate"
          "details details"`}
          gridTemplateColumns="1fr 1fr"
          gridTemplateRows="auto auto"
          rowGap="16px"
          columnGap="16px"
        >
          <GridItem gridArea="originalEndDate">
            <DateDisplay
              label="Original End Date"
              date={originalEndDate}
              data-testid="cancelConfirmation.originalEndDate"
            />
          </GridItem>
          <GridItem gridArea="newEndDate">
            <DateDisplay
              label="New End Date"
              date={newEndDate}
              data-testid="cancelConfirmation.newEndDate"
            />
          </GridItem>
          <GridItem gridArea="details">
            <DetailRow
              label={'Customer'}
              value={
                <Flex gap="6px">
                  {customer.logoSrc && (
                    <Img src={customer.logoSrc} height="16px" width="16px" />
                  )}
                  {customer.name}
                </Flex>
              }
            />
            <DetailRow
              label={'Start date'}
              value={startDate && format(startDate, DATE_FORMAT_WITH_YEARS)}
            />
            <DetailRow
              label={'Original end date'}
              value={
                originalEndDate
                  ? format(originalEndDate, DATE_FORMAT_WITH_YEARS)
                  : 'Ongoing'
              }
            />
            <DetailRow
              label={'New end date'}
              value={newEndDate && format(newEndDate, DATE_FORMAT_WITH_YEARS)}
            />
          </GridItem>
        </Grid>
      </Modal.Content>
      <Footer
        destructive={cancelType === 'immediate'}
        onBack={onBack}
        onContinue={onSubmit}
        disabled={!newEndDate}
        submitting={submitting}
        submitButtonText={match(cancelType)
          .with('immediate', () =>
            submitting ? 'Cancelling schedule...' : 'Cancel Schedule'
          )
          .with('endOfBillingPeriod', 'customDate', () => 'Continue')
          .exhaustive()}
      />
    </>
  )
}
